import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { ConfirmationConfig } from '../../shared/confirmation-modal/confirmation-modal.component';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-business-income-receipts',
  templateUrl: './business-income-receipts.component.html',
  styleUrl: './business-income-receipts.component.scss'
})
export class BusinessIncomeReceiptsComponent implements OnInit, OnDestroy {
  incomeReceipts: any[] = [];
  filteredReceipts: any[] = [];

  // Debounce (mismo patrón que la vista individual)
  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Filtros
  searchTerm: string = '';
  filterId: number | null = null; // 🆕 Filtro por ID de comprobante (espejo de la vista individual)
  selectedPaymentMethod: any = null; // 🔄 Cambiado para p-dropdown
  selectedPaymentType: any = null; // 🔄 Cambiado para p-dropdown
  selectedCancellationFilter: string = 'active'; // 🆕 Filtro de anulación

  /**
   * 🆕 Filtro adicional por proyecto (numeric id). Se setea desde deep-link
   * `?projectId=NN` (ej: payment-plan-audit). Se aplica client-side sobre
   * los resultados ya paginados del servidor.
   */
  filterProjectId: number | null = null;

  // 🆕 Filtros de fecha
  startDate: Date | null = null;
  endDate: Date | null = null;
  dateType: string = 'Creation'; // 'Creation' o 'Payment'
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];

  // 🆕 Ordenamiento
  sortOrder: 'asc' | 'desc' = 'desc'; // Por defecto descendente (más recientes primero)

  // 🆕 Paginación
  pageNumber: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  paginatedReceipts: any[] = [];

  // Métodos de pago únicos
  paymentMethods: any[] = [];

  // 🆕 Modal de confirmación de anulación
  showConfirmationModal = false;
  cancellationReason = '';
  selectedReceiptForCancellation: any = null;

  confirmationConfig: ConfirmationConfig = {
    title: '¿Anular comprobante de ingreso?',
    message: 'Esta acción es irreversible. El comprobante será anulado y no se incluirá en reportes ni sumas.',
    confirmText: 'Anular Comprobante',
    cancelText: 'Cancelar',
    type: 'danger',
    icon: 'fa-ban',
    showIcon: true
  };

  // Métricas generales
  totalGross: number = 0;
  totalNet: number = 0;
  totalDiscounts: number = 0;
  avgPaymentSpeed: number = 0;

  // Métricas específicas por vista
  corporateReceipts: number = 0;

  constructor(
    private incomeReceiptsService: IncomeReceiptsService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // 🆕 Acepta query param ?search=XXX para precargar el filtro de búsqueda
    // (útil para deep-links desde otros dashboards, ej: payment plan audit).
    const initialSearch = this.route.snapshot.queryParamMap.get('search');
    if (initialSearch) {
      this.searchTerm = initialSearch;
    }

    // 🆕 ?projectId=NN — co-filtro por proyecto (client-side). Llega desde
    // payment-plan-audit para acotar los pagos a un único proyecto.
    const initialProjectId = this.route.snapshot.queryParamMap.get('projectId');
    if (initialProjectId && !isNaN(Number(initialProjectId))) {
      this.filterProjectId = Number(initialProjectId);
    }

    // 🆕 Debounce de filtros (espejo de la vista individual): el request se
    // dispara 400 ms después de la última interacción, no por cada tecla.
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 🆕 MÉTODOS DE IDENTIFICACIÓN BASADOS EN PayerType
  isCorporateReceipt(receipt: any): boolean {
    // Verificar primero si existe payerType (nuevo backend) 
    if (receipt.payerType !== undefined) {
      return receipt.payerType === 'COMPANY';
    }
    // Fallback para datos existentes (no participant, pero puede tener company info)
    return receipt.participant === null;
  }

  isParticipantCorporateReceipt(receipt: any): boolean {
    // Verificar primero si existe payerType (nuevo backend)
    if (receipt.payerType !== undefined) {
      return receipt.payerType === 'PARTICIPANT_CORPORATE';
    }
    // Fallback para datos existentes (participant Y company)  
    return receipt.participant !== null && receipt.companyId;
  }

  getPayerDisplayText(receipt: any): string {
    if (receipt.payerDisplayName) {
      return receipt.payerDisplayName; // 🆕 Usar el campo calculado del backend
    }

    // 🔄 FALLBACK para datos existentes
    if (this.isParticipantCorporateReceipt(receipt)) {
      const participantName = `${receipt.participant?.firstName || ''} ${receipt.participant?.lastName || ''}`.trim();
      const companyName = receipt.companyName || receipt.company?.name || 'Empresa';
      return `${participantName} (${companyName})`;
    } else if (this.isCorporateReceipt(receipt)) {
      return receipt.companyName || receipt.company?.name || 'Pago Empresarial';
    } else {
      return `${receipt.participant?.firstName || ''} ${receipt.participant?.lastName || ''}`.trim() || 'Participante';
    }
  }

  private loadData(): void {
    this.loadingService.show();

    // 🆕 USAR SERVICIO PAGINADO DEL LADO SERVIDOR
    const parameters: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      sortBy: 'Id',
      sortOrder: this.sortOrder,
      startDate: this.startDate,
      endDate: this.endDate,
      dateType: this.dateType,
      cancellationFilter: this.selectedCancellationFilter,
      paymentMethodId: this.selectedPaymentMethod,
      isManualPayment: this.selectedPaymentType,
      payerType: 'COMPANY' // Solo corporativas en esta vista
    };

    if (this.filterId) parameters.filterId = this.filterId;

    this.incomeReceiptsService.getIncomeReceiptsPaged(parameters).subscribe({
      next: (response) => {
        this.incomeReceipts = response.data || [];
        this.totalRecords = response.totalRecords || 0;

        // 🆕 Co-filtro client-side por proyecto cuando el deep-link lo pide.
        // Ver comentario en `filterProjectId`.
        if (this.filterProjectId) {
          this.incomeReceipts = this.incomeReceipts.filter(
            (r: any) => r?.project?.id === this.filterProjectId
          );
          this.totalRecords = this.incomeReceipts.length;
        }

        this.paginatedReceipts = this.incomeReceipts; // Ya viene paginado del servidor

        this.processData();
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error al cargar comprobantes paginados:', error);
        this.toastService.showToast('error', 'Error al cargar los datos');
        this.loadingService.hide();
      }
    });
  }

  private processData(): void {
    // 🆕 CALCULAR MÉTRICAS ESPECÍFICAS PARA VISTA EMPRESARIAL
    this.corporateReceipts = this.incomeReceipts.filter(r =>
      this.isCorporateReceipt(r) || this.isParticipantCorporateReceipt(r)
    ).length;

    // Obtener métodos de pago únicos
    const uniqueMethodIds = [...new Set(this.incomeReceipts
      .filter(r => r.paymentMethod) // Filtrar receipts que tengan paymentMethod
      .map(r => r.paymentMethod.id))];

    this.paymentMethods = uniqueMethodIds.map(id =>
      this.incomeReceipts.find(r => r.paymentMethod && r.paymentMethod.id === id)?.paymentMethod
    ).filter(method => method); // Filtrar métodos undefined

    // Calcular métricas para la vista actual
    this.calculateCurrentViewMetrics();
  }

  private calculateCurrentViewMetrics(): void {
    // 🆕 FILTRAR DATOS PARA VISTA EMPRESARIAL
    const currentViewData = this.incomeReceipts.filter(receipt =>
      this.isCorporateReceipt(receipt) || this.isParticipantCorporateReceipt(receipt)
    );

    // Calcular métricas para la vista actual
    this.totalGross = currentViewData.reduce((sum, r) => sum + r.grossAmount, 0);
    this.totalNet = currentViewData.reduce((sum, r) => sum + r.netAmount, 0);
    this.totalDiscounts = currentViewData.reduce((sum, r) => sum + r.totalFees, 0);

    // Calcular velocidad promedio de pago para vista actual
    const totalDays = currentViewData
      .filter(r => r.processingDays)
      .reduce((sum, r) => sum + r.processingDays, 0);
    this.avgPaymentSpeed = totalDays / currentViewData.length || 0;
  }

  applyFilters(): void {
    this.filteredReceipts = this.incomeReceipts.filter(receipt => {
      const matchesSearch = Object.values(receipt).some(value =>
        value && String(value).toLowerCase().includes(this.searchTerm.toLowerCase())
      );

      const matchesMethod = this.selectedPaymentMethod ?
        (receipt.paymentMethod && receipt.paymentMethod.id == this.selectedPaymentMethod) : true;

      const matchesType = this.selectedPaymentType !== null ?
        receipt.isManualPayment === this.selectedPaymentType : true;

      // 🆕 FILTRO SOLO PARA VISTA EMPRESARIAL
      const matchesView = this.isCorporateReceipt(receipt) || this.isParticipantCorporateReceipt(receipt);

      // 🆕 FILTRO POR ESTADO DE ANULACIÓN (no necesario si ya se cargaron filtrados desde el backend)
      // Pero se mantiene para consistencia en caso de que los datos ya estén en memoria
      const matchesCancellation = this.selectedCancellationFilter === 'all' ? true :
        this.selectedCancellationFilter === 'cancelled' ? receipt.isCancelled === true :
          receipt.isCancelled !== true; // 'active'

      // 🆕 FILTRO POR RANGO DE FECHAS
      const matchesDateRange = this.checkDateRange(receipt);

      return matchesSearch && matchesMethod && matchesType && matchesView && matchesCancellation && matchesDateRange;
    });

    // 🆕 Aplicar ordenamiento por ID
    this.filteredReceipts.sort((a, b) => {
      return this.sortOrder === 'desc' ? b.id - a.id : a.id - b.id;
    });

    // 🆕 Aplicar paginación después de filtros
    this.totalRecords = this.filteredReceipts.length;
    this.applyPagination();
  }

  // 🆕 Método para verificar rango de fechas según tipo
  private checkDateRange(receipt: any): boolean {
    if (!this.startDate && !this.endDate) return true;

    // Seleccionar la fecha según el tipo
    const dateToCheck = this.dateType === 'Payment'
      ? receipt.transactionDate
      : receipt.createdAt;

    if (!dateToCheck) return true;

    const receiptDate = new Date(dateToCheck);
    receiptDate.setHours(0, 0, 0, 0);

    if (this.startDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);
      if (receiptDate < start) return false;
    }

    if (this.endDate) {
      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      if (receiptDate > end) return false;
    }

    return true;
  }

  // 🆕 Método para aplicar paginación
  applyPagination(): void {
    const startIndex = (this.pageNumber - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedReceipts = this.filteredReceipts.slice(startIndex, endIndex);
  }

  // 🆕 Método para cambio de página
  onPageChange(event: any): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadData();
  }

  // 🆕 Método para cambio de filtros (con debounce; reinicia paginación en el handler suscrito)
  onFilterChange(): void {
    this.filterSubject.next();
  }

  // 🆕 Método para cambiar ordenamiento
  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.loadData();
  }

  // 🆕 Método para obtener documento de la empresa
  getCompanyDocument(receipt: any): string {
    if (receipt.company) {
      return `${receipt.company.documentId || 'N/A'}`;
    }
    return 'N/A';
  }

  // 🆕 MÉTODO PARA CAMBIAR FILTRO DE ANULACIÓN
  onCancellationFilterChange(): void {
    this.pageNumber = 1; // Reinicia paginación (espejo de la vista individual) para no quedar en página fuera de rango
    this.loadData(); // Recargar datos según el filtro seleccionado
  }

  // 🆕 MÉTODOS PARA MANEJO DE ANULACIÓN
  showCancelModal(receipt: any): void {
    if (receipt.isCancelled) {
      this.toastService.showToast('warning', 'Este comprobante ya está anulado');
      return;
    }

    this.selectedReceiptForCancellation = receipt;
    this.cancellationReason = '';
    this.showConfirmationModal = true;
  }

  async onConfirmCancellation(): Promise<void> {
    if (!this.cancellationReason?.trim()) {
      this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }

    if (!this.selectedReceiptForCancellation) {
      return;
    }

    try {
      this.loadingService.show();

      await this.incomeReceiptsService.cancelIncomeReceipt(
        this.selectedReceiptForCancellation.id,
        this.cancellationReason.trim()
      ).toPromise();

      this.toastService.showToast('success', '✅ Comprobante anulado exitosamente');
      this.closeConfirmationModal();
      this.loadData(); // Recargar datos para reflejar cambios

    } catch (error: any) {
      console.error('Error al anular comprobante:', error);
      const errorMessage = error?.error?.errorMessage ||
        error?.error ||
        'Error al anular el comprobante';
      this.toastService.showToast('error', errorMessage);
    } finally {
      this.loadingService.hide();
    }
  }

  onCancelCancellation(): void {
    this.closeConfirmationModal();
  }

  private closeConfirmationModal(): void {
    this.showConfirmationModal = false;
    this.selectedReceiptForCancellation = null;
    this.cancellationReason = '';
  }

  getPaymentProgress(receipt: any): number {
    return (receipt.netAmount / receipt.grossAmount) * 100;
  }

  // 🆕 Método para formatear montos con moneda específica del comprobante
  formatCurrencyValue(amount: number, receipt: any): string {
    if (!amount && amount !== 0) return '';

    // Prioridad: moneda del comprobante > COP por defecto
    const currencySymbol = receipt?.currencySymbol || '$';
    const currencyCode = receipt?.currencyCode || 'COP';

    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  // 🆕 Verificar si el comprobante tiene tasa de cambio internacional
  hasExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1;
  }

  // 🆕 Obtener el equivalente en COP si hay tasa de cambio
  getCOPEquivalent(amount: number, receipt: any): string {
    if (!this.hasExchangeRate(receipt) || !receipt?.exchangeRate) {
      return '';
    }

    const copAmount = amount * receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }

  // 🆕 MÉTODOS DE DESCARGA DE PDF
  isDownloadingPdf: { [key: number]: boolean } = {};

  downloadPdf(receipt: any): void {
    if (this.isDownloadingPdf[receipt.id]) return;

    this.isDownloadingPdf[receipt.id] = true;
    this.incomeReceiptsService.downloadPdf(receipt.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ComprobanteIngreso_${receipt.id}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf[receipt.id] = false;
        this.toastService.showToast('success', 'PDF descargado correctamente');
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.isDownloadingPdf[receipt.id] = false;
        this.toastService.showToast('error', 'Error al descargar el PDF');
      }
    });
  }

  // 🆕 MÉTODOS PARA INDICADORES DE APROBACIÓN
  getApprovalStatusInfo(receipt: any): { label: string; cssClass: string; icon: string } | null {
    if (!receipt.approvalStatus || receipt.approvalStatus === 'None') {
      return null;
    }
    switch (receipt.approvalStatus) {
      case 'PendingApproval':
        return { label: 'Pendiente Aprobación', cssClass: 'bg-yellow-100 text-yellow-800', icon: 'fa-clock' };
      case 'Approved':
        return { label: 'Aprobado', cssClass: 'bg-green-100 text-green-800', icon: 'fa-check-circle' };
      case 'Rejected':
        return { label: 'Rechazado', cssClass: 'bg-red-100 text-red-800', icon: 'fa-times-circle' };
      default:
        return null;
    }
  }

  isPendingApproval(receipt: any): boolean {
    return receipt.approvalStatus === 'PendingApproval';
  }

  hasVersionInfo(receipt: any): boolean {
    return receipt.version && receipt.version > 1;
  }
}
